// 各ページのタイトル・説明文・正式な URL（canonical のパス）の正本。
// ページは usePageMeta(PAGE_META.xxx) でこれを設定し、ビルド後のプリレンダー（scripts/prerender.js）は、
// 画面が設定した値をそのまま最初の HTML に書く。ここを直せば、最初の HTML と画面の両方が同じに変わる。
// （ニュース・ポッドキャストの回・内製化支援のよくある質問は、それぞれのページのファイルに書いている）
//
// 社名は「株式会社SALT2」と書き、「AI」と組にする。「SALT2」だけだと、天文学の SALT2 など別のものと混ざるため
// （検索と AI の回答についての調査、2026-10-18）。タイトルに「AI」が無いページは「AIの会社 株式会社SALT2」で終える
// （「AIの会社」はトップの説明文と同じ言い方）。LOCKED IN は別ブランドなので、名前は data/communityMeta.json のまま。
import { cases } from './consulting';
import { DOCUMENT_PAGES, documentPath } from './documentRequests';
import { PLAN_PATH } from './enablement';
import { positions } from './careersJobs';
import { JOB_TITLE_JA } from './jobPostings';
import communityMeta from './communityMeta.json';
import { careersJobPath } from '../lib/routes';

export const COMPANY = '株式会社SALT2';

/** 「ページ名 | … | 株式会社SALT2」。どこにも「AI」が無ければ「AIの会社 株式会社SALT2」で終える */
export const pageTitle = (...parts) => {
  const plain = [...parts, COMPANY].join(' | ');
  return /AI/.test(plain) ? plain : [...parts, `AIの会社 ${COMPANY}`].join(' | ');
};

export const PAGE_META = {
  home: {
    title: `${COMPANY} | AIエージェント製品Trooper・AI内製化支援・AIコンサルティング`,
    description: `${COMPANY}は、AIをつくる力と、企業の仕事に届ける力の両方を持つAIの会社です。AIエージェント製品「Trooper」の開発・提供、AI内製化支援、AIコンサルティング・開発の3つの事業で、企業の仕事の成果が出るところまで担います。`,
    path: '/',
  },
  enablement: {
    title: pageTitle('AI内製化支援', '法人向けAI研修・AI人材育成'),
    description: `${COMPANY}のAI内製化支援。企業が、自らAIで仕事を変えられるように。ワークショップ、FDEチーム、内製化支援プログラムの3つの形で支援します。目指すのは、作れる人が育つことと、貴社の方が実際の業務のユースケースを作り、実装まで進めることです。`,
    path: '/enablement',
  },
  enablementPlan: {
    title: pageTitle('貴社に合うプランを見つける', 'AI内製化支援'),
    description: `${COMPANY}のAI内製化支援。困りごと、いまの状態、進め方を選ぶと、貴社に合うAI内製化の支援と、目指す姿・最初にできること・進め方をご覧いただけます。`,
    path: PLAN_PATH,
  },
  // 検索の言葉ごとのページ（2026-10-19 代表指示。中身は data/enablementTopics.js。パスは同じファイルの TOPIC_PATHS と同じ）。
  // 説明文の事実（期間・人数・事例）は、そのファイルと同じ出典（営業資料 v0.1・提案テンプレート v0.4・2026-10-20 代表の回答）から取る。
  // 料金は書かない（2026-10-20 代表指示）
  enablementClaudeCode: {
    title: pageTitle('Claude Code 法人研修', 'AI駆動開発の研修', 'AI内製化支援'),
    description: `${COMPANY}のClaude Code 法人研修。エンジニアも非エンジニアも、Claude Code で業務で動くアプリやAIエージェントを作り切ることを目指します。作れる人が育ち、貴社の方が実際の業務のユースケースを実装まで進めることを目指して、毎週の講義とハンズオンを対面で行います。進め方と事例をご紹介します。`,
    path: '/enablement/claude-code',
  },
  enablementAiAgent: {
    title: pageTitle('AIエージェント研修（法人向け）', 'AI内製化支援'),
    description: `${COMPANY}の法人向けAIエージェント研修。受講者が自分の業務をお題に、業務を任せるAIエージェントや自動の流れを作り、ユースケースとして実装まで進めることを目指します。対象と使えるAIに合わせた3つの作り方、進め方、安全に任せるための講義の例、事例をご紹介します。`,
    path: '/enablement/ai-agent',
  },
  enablementNonEngineer: {
    title: pageTitle('非エンジニア向けAI開発研修', 'AI内製化支援'),
    description: `${COMPANY}の非エンジニア向けAI開発研修。管理職や業務の担当者が、要件定義と発注・自分たちでつくる・いまのAIで効率化の3つの型から選んで学びます。大手メーカーA社では、マネージャー層7名（非エンジニアが中心）が7つのアプリを作り、2つが本番化に進んでいます。`,
    path: '/enablement/non-engineer',
  },
  cases: {
    title: pageTitle('AIコンサルティング・開発'),
    description: `${COMPANY}のAIコンサルティング・開発。課題の特定から、実装・定着まで。FDEチームが現場に入り、伺ったその場で作ります。Boostグループと一つのチームで、AIを使われ続ける形にします。`,
    path: '/cases',
  },
  about: {
    title: pageTitle('会社概要'),
    description: `AIをつくる力と、企業の仕事に届ける力。その両方を持つ。${COMPANY}のビジョン・ミッション・バリュー、メンバー、会社概要。`,
    path: '/about',
  },
  contact: {
    title: pageTitle('お問い合わせ・資料請求'),
    description: `AIの会社、${COMPANY}へのご相談、協業のご提案、取材のご依頼、資料請求はこちらから。初回のご相談は無料です。`,
    path: '/contact',
  },
  documents: {
    title: pageTitle('資料請求'),
    description: `${COMPANY}の、AIエージェント製品 Trooper、AI内製化支援、AIコンサルティング・開発の資料請求。`,
    path: '/documents',
  },
  careers: {
    title: pageTitle('採用サイト'),
    description: `人生の勝負所に、本気で向き合う。AIの会社、${COMPANY}の採用サイト。エンジニア向け・コンサルタント・ビジネス向けの会社紹介資料、メンバー、募集要項。`,
    path: '/careers',
  },
  community: {
    title: communityMeta.title,
    description: communityMeta.description,
    path: communityMeta.path,
    ogTitle: communityMeta.ogTitle,
    ogDescription: communityMeta.ogDescription,
    image: communityMeta.ogImage,
    siteName: 'LOCKED IN',
    themeColor: communityMeta.themeColor,
  },
  privacyPolicy: {
    title: pageTitle('プライバシーポリシー'),
    description: `AIの会社、${COMPANY}のプライバシーポリシー。当社が提供するサービスと、当社が運営するウェブサイトにおける情報の取扱いを定めています。`,
    path: '/privacy-policy',
  },
  termsOfService: {
    title: pageTitle('利用規約'),
    description: `AIの会社、${COMPANY}が提供するコンテンツ管理・配信サービスの利用条件を定める利用規約です。`,
    path: '/terms-of-service',
  },
  // 検索に載せないページ（canonical なし・noindex）
  notFound: {
    title: `ページが見つかりません | ${COMPANY}`,
    description: 'お探しのページは見つかりませんでした。',
    path: null,
    robots: 'noindex',
  },
  tiktokCallback: {
    title: `TikTok Authorization | ${COMPANY}`,
    description: 'TikTok の認可の結果を表示する画面です。',
    path: null,
    robots: 'noindex',
  },
};

/** 事例のページ（/cases/case-1 など）。文言は data/consulting.js の事例から作り、数字や事実を足さない */
export const caseMeta = (c) => {
  const h = c.headline;
  return {
    title: pageTitle(`事例：${c.client}`, 'AIコンサルティング・開発'),
    description: `${COMPANY}のAIコンサルティング・開発の事例。${c.client}（${c.meta.join('、')}）。${c.built.label}：${c.built.text}。${h.label}：${h.prefix ?? ''}${h.value}${h.unit}（${h.note}）。`,
    path: `/cases/${c.id}`,
  };
};

/** 製品ごとの資料請求のページ（/documents/trooper など）。page は data/documentRequests.js の DOCUMENT_PAGES */
export const documentMeta = (page) => {
  const name = page.kind && !page.product.includes(page.kind) ? `${page.product}（${page.kind}）` : page.product;
  return {
    title: pageTitle(`${name}の資料請求`),
    description: `${COMPANY}の${name}の資料をお送りします。フォームを送信すると、その場でダウンロードできます。`,
    path: documentPath(page.slug),
  };
};

/** 募集ごとのページ（/careers/jobs/ai-product-builder など）。文言は data/careersJobs.js の募集のまま */
export const jobMeta = (job) => ({
  title: pageTitle(JOB_TITLE_JA[job.id], job.title, `${job.type}の募集要項`, '採用サイト'),
  description: `AIの会社、${COMPANY}の${job.type}の募集要項。${JOB_TITLE_JA[job.id]}（${job.title}）。${job.catch}${job.summary}`,
  path: careersJobPath(job.id),
});

/** 書き出すページのうち、ここで名前を決めているもの（テストで、社名と「AI」の書き方を確かめる） */
export const allPageMeta = () => [
  ...Object.values(PAGE_META),
  ...cases.map(caseMeta),
  ...Object.values(DOCUMENT_PAGES).map(documentMeta),
  ...positions.map(jobMeta),
];
